import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import BidBoard from '@/components/bid/BidBoard';
import BidPackageView from '@/components/bid/BidPackageView';
import '@/styles/site-office.css';
import axios from 'axios';
import { 
  ArrowLeft, Package, Plus, FileText, Users, Send, 
  ChevronRight, ChevronDown, Search, MoreVertical,
  Clock, CheckCircle, DollarSign, Trophy, Layers,
  Eye, Edit, Trash2, BarChart3, Calendar, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PagePurpose, NextStepPrompt } from '@/components/UXGuide';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500', icon: Edit },
  sent: { label: 'Sent', color: 'bg-blue-500', icon: Send },
  bids_received: { label: 'Bids Received', color: 'bg-yellow-500', icon: DollarSign },
  awarded: { label: 'Awarded', color: 'bg-green-500', icon: Trophy },
  closed: { label: 'Closed', color: 'bg-gray-600', icon: CheckCircle },
};

const TRADES = [
  { value: 'general', label: 'General Construction' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'carpentry', label: 'Carpentry' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'painting', label: 'Painting' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'landscaping', label: 'Landscaping' },
];

export default function BidPackagesPage() {
  const { projectId } = useParams();
  // DP269-D1 — what "Send the request" tells this page on arrival.
  const [boardParams] = useSearchParams();
  const sentId = boardParams.get('sent');
  const sentN = boardParams.get('n') || '0';
  const sentA = boardParams.get('a') || '0';
  // DP269-D2 — a board row opens the package above the board.
  const openId = boardParams.get('open');
  const [firmShort, setFirmShort] = useState('Your firm');
  const [firmName, setFirmName] = useState('Your firm');
  useEffect(() => {
    axios.get(`${API}/company-settings`)
      .then((r) => {
        const name = String(r.data?.firm_name || r.data?.company_name || 'Your firm');
        setFirmName(name);
        setFirmShort(name.split(' ')[0]);
      })
      .catch(() => {});
  }, []);
  const navigate = useNavigate();
  
  // Dark mode state - sync with system preference
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('keyMadeDarkMode') === 'true';
    }
    return false;
  });
  
  const [packages, setPackages] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [drawings, setDrawings] = useState([]);
  const [contractors, setContractors] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tradeFilter, setTradeFilter] = useState('all');
  
  // Create Package Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPackage, setNewPackage] = useState({
    title: '',
    description: '',
    trade: 'general',
    bid_due_date: ''
  });
  
  // Create from Scope Dialog
  const [showFromScopeDialog, setShowFromScopeDialog] = useState(false);
  const [scopeByTrade, setScopeByTrade] = useState({ trades: {}, summary: {} });
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [loadingScopeData, setLoadingScopeData] = useState(false);
  
  // View Package Dialog  
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  // Add Scope Dialog
  const [showScopeDialog, setShowScopeDialog] = useState(false);
  const [newScope, setNewScope] = useState({
    title: '',
    description: '',
    inclusions: '',
    exclusions: ''
  });
  
  // Add Drawings Dialog
  const [showDrawingsDialog, setShowDrawingsDialog] = useState(false);
  const [selectedDrawingIds, setSelectedDrawingIds] = useState([]);
  
  // Send Dialog
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [attachPdf, setAttachPdf] = useState(true);
  const [sendingPackage, setSendingPackage] = useState(false);
  
  // Compare Dialog
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [comparison, setComparison] = useState(null);
  
  // Listen for dark mode changes from other components
  useEffect(() => {
    const handleStorage = () => {
      setDarkMode(localStorage.getItem('keyMadeDarkMode') === 'true');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(() => {
      const current = localStorage.getItem('keyMadeDarkMode') === 'true';
      if (current !== darkMode) setDarkMode(current);
    }, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [darkMode]);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [packagesRes, projectRes, drawingsRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/bid-packages`),
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/drawings`).catch(() => ({ data: { drawings: [] }})),
        axios.get(`${API}/contractors`).catch(() => ({ data: [] }))
      ]);
      
      setPackages(packagesRes.data.bid_packages || []);
      setSummary(packagesRes.data.summary || {});
      setProject(projectRes.data);
      setDrawings(drawingsRes.data.drawings || []);
      setContractors(Array.isArray(contractorsRes.data) ? contractorsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load bid packages');
    } finally {
      setLoading(false);
    }
  };

  const openFromScopeDialog = async () => {
    setLoadingScopeData(true);
    setShowFromScopeDialog(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/scope-by-trade`);
      setScopeByTrade(response.data);
    } catch (error) {
      console.error('Error loading scope data:', error);
      toast.error('Failed to load scope data');
    } finally {
      setLoadingScopeData(false);
    }
  };

  const handleCreateFromScope = async () => {
    if (!selectedTrade) {
      toast.error('Please select a trade');
      return;
    }
    
    try {
      const tradeInfo = scopeByTrade.trades[selectedTrade];
      const response = await axios.post(`${API}/projects/${projectId}/bid-packages/from-scope`, {
        trade: selectedTrade,
        title: `${TRADES.find(t => t.value === selectedTrade)?.label || selectedTrade} Bid Package`,
        description: `Scope of work for ${tradeInfo.rooms.join(', ')}`
      });
      
      toast.success(`Bid Package ${response.data.bid_package.package_number} created with scope items`);
      setShowFromScopeDialog(false);
      setSelectedTrade(null);
      fetchData();
    } catch (error) {
      console.error('Error creating bid package from scope:', error);
      toast.error(error.response?.data?.detail || 'Failed to create bid package');
    }
  };

  const handleCreatePackage = async () => {
    if (!newPackage.title) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/projects/${projectId}/bid-packages`, newPackage);
      toast.success(`Bid Package ${response.data.bid_package.package_number} created`);
      setShowCreateDialog(false);
      setNewPackage({ title: '', description: '', trade: 'general', bid_due_date: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating package:', error);
      toast.error('Failed to create bid package');
    }
  };

  const viewPackage = async (pkg) => {
    try {
      const response = await axios.get(`${API}/bid-packages/${pkg.id}`);
      setSelectedPackage(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load package details');
    }
  };

  const handleAddScope = async () => {
    if (!selectedPackage || !newScope.title) {
      toast.error('Please enter a scope title');
      return;
    }
    
    try {
      await axios.post(`${API}/bid-packages/${selectedPackage.id}/add-scope`, {
        title: newScope.title,
        description: newScope.description,
        inclusions: newScope.inclusions.split('\n').filter(Boolean),
        exclusions: newScope.exclusions.split('\n').filter(Boolean)
      });
      
      toast.success('Scope section added');
      setShowScopeDialog(false);
      setNewScope({ title: '', description: '', inclusions: '', exclusions: '' });
      
      // Refresh package
      const response = await axios.get(`${API}/bid-packages/${selectedPackage.id}`);
      setSelectedPackage(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to add scope');
    }
  };

  const openDrawingsDialog = (pkg) => {
    setSelectedPackage(pkg);
    // Pre-select already attached drawings
    const existingIds = (pkg.drawings || []).map(d => d.drawing_id);
    setSelectedDrawingIds(existingIds);
    setShowDrawingsDialog(true);
  };

  const handleAddDrawings = async () => {
    if (!selectedPackage) return;
    
    // Find new drawings (not already in package)
    const existingIds = (selectedPackage.drawings || []).map(d => d.drawing_id);
    const newIds = selectedDrawingIds.filter(id => !existingIds.includes(id));
    
    if (newIds.length === 0) {
      toast.info('No new drawings to add');
      setShowDrawingsDialog(false);
      return;
    }
    
    try {
      await axios.post(`${API}/bid-packages/${selectedPackage.id}/add-drawings`, {
        drawing_ids: newIds
      });
      
      toast.success(`Added ${newIds.length} drawing(s)`);
      setShowDrawingsDialog(false);
      
      // Refresh package
      const response = await axios.get(`${API}/bid-packages/${selectedPackage.id}`);
      setSelectedPackage(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to add drawings');
    }
  };

  const openSendDialog = (pkg) => {
    setSelectedPackage(pkg);
    setSelectedContractors([]);
    setAttachPdf(true);
    setShowSendDialog(true);
  };

  const handleSendPackage = async () => {
    if (selectedContractors.length === 0) {
      toast.error('Please select at least one contractor');
      return;
    }
    
    try {
      setSendingPackage(true);
      const response = await axios.post(`${API}/bid-packages/${selectedPackage.id}/send`, {
        contractor_ids: selectedContractors,
        send_email: true,
        attach_pdf: attachPdf
      });
      
      const pdfNote = response.data.pdf_attached ? ' with PDF attachment' : '';
      toast.success(`Bid package sent to ${selectedContractors.length} contractor(s)${pdfNote}`, {
        description: `${response.data.emails_sent} email(s) sent successfully.`
      });
      setShowSendDialog(false);
      setSelectedContractors([]);
      setAttachPdf(true);
      fetchData();
    } catch (error) {
      toast.error('Failed to send bid package');
    } finally {
      setSendingPackage(false);
    }
  };

  const openComparison = async (pkg) => {
    try {
      const response = await axios.get(`${API}/bid-packages/${pkg.id}/compare`);
      setComparison(response.data);
      setShowCompareDialog(true);
    } catch (error) {
      toast.error('Failed to load comparison');
    }
  };

  const handleExportPDF = async (pkg) => {
    try {
      toast.info('Generating PDF...', { duration: 2000 });
      const response = await axios.get(`${API}/bid-packages/${pkg.id}/export-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pkg.package_number}_${pkg.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  };

  const filteredPackages = packages.filter(p => {
    const matchesSearch = !searchQuery || 
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.package_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchesTrade = tradeFilter === 'all' || p.trade === tradeFilter;
    return matchesSearch && matchesStatus && matchesTrade;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]'}`} data-testid="bid-packages-page">
      {/* Header */}
      <header className={`sticky top-0 z-40 border-b backdrop-blur ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]/95' : 'border-[#E5E2DD] bg-white/95'}`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
          <div className="flex flex-wrap items-center justify-between gap-y-3">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(projectId ? `/projects/${projectId}/hub` : '/')}
                className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {project?.name || 'Back'}
              </Button>
              <div className={`h-6 w-px ${darkMode ? 'bg-[#1A1A1A]' : 'bg-[#E5E2DD]'}`} />
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-[#FDF8F6]'}`}>
                  <Package className="w-5 h-5 text-[#BC5D41]" />
                </div>
                <div className="flex items-center gap-2">
                  <HelpIcon elementId="bid-request" position="inline" />
                  <div>
                    <h1 className={`font-heading font-light text-xl ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>Bid Packages</h1>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>
                      {summary.total || 0} packages • {summary.bids_received || 0} with bids
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate(`/projects/${projectId}/ask-for-a-price`)}
                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                data-testid="bid-packages-new"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Package
              </Button>
            </div>
          </div>
        </div>
      </header>
      {/* DP269-D1 — the board is the page. The old package cards, dialogs and
          filters above stay in this file, unused, for D2 to draw on. */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-6">
        {openId ? (
          <>
            <div className="kso-acts" style={{ marginBottom: 'var(--km-s3)' }}>
              <button type="button" className="kso-btn" data-testid="bid-package-back"
                onClick={() => navigate(`/projects/${projectId}/bid-packages`)}>Back to the board</button>
            </div>
            <BidPackageView projectId={projectId} packageId={openId} firmShort={firmShort} firmName={firmName} />
          </>
        ) : null}
        {sentId ? (
          <p className="kso-hint" data-testid="bid-packages-sent-line">
            Sent to {sentN} · {sentA} will see the agreement first
          </p>
        ) : null}
        <BidBoard projectId={projectId} />
      </div>
    </div>
  );
}
